import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from '../../../../../../ui/toast';
import { getDashboardUsers } from '../../../../../../utils/api_service/dashboard';
import { UserData } from '../../../../../../utils/types/DashboardTypes';

export type TableColumn = {
  title: string;
  width: string;
};

export type DashboardUserData = {
  users: UserData[];
  totalUsers: number;
};

export const useUsersTab = (externalSearchTerm?: string) => {
  const history = useHistory();
  const [usersData, setUsersData] = useState<UserData[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);

  // ✅ Function to fetch users (no hooks inside)
  const fetchUsers = async (page: number) => {
    setLoading(true);
    try {
      const response = await getDashboardUsers(externalSearchTerm ?? '', page);
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      const data: DashboardUserData = await response.json();
      setTotalPages(Math.ceil(data.totalUsers / 5));
      setUsersData(data.users);
      setTotalRecords(data.totalUsers);
    } catch (error) {
      toast.error('Error Fetching User Data', { position: 'bottom-center' });
    } finally {
      setLoading(false);
    }
  };

  // ✅ Use effect to trigger fetchUsers when dependencies change
  useEffect(() => {
    const debounceSearch = setTimeout(() => {
      fetchUsers(pageNumber);
    }, 1000);

    return () => clearTimeout(debounceSearch);
  }, [externalSearchTerm, pageNumber]);

  // ✅ Handle page changes
  const onPageChange = (page: number) => {
    setPageNumber(page);
  };

  return {
    searchTerm: externalSearchTerm,
    history,
    usersData,
    pageNumber,
    totalPages,
    loading,
    setUsersData,
    onPageChange,
    totalRecords,
  };
};
